import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChipOption } from './ChipMultiSelect';

interface Props {
    /** Flat catalog of available processes (sourced from the API via fetchResponsibilities). */
    options: ChipOption[];
    /** Ordered sequence currently in use — the production cascade. */
    selected: ChipOption[];
    onChange: (next: ChipOption[]) => void;
    /**
     * 'production' (default) = ordered production sequence ("Rendi i prodhimit",
     * steps). 'consume' = the KuBIT raw-material picker where the chosen processes
     * are just a SET (order irrelevant) of stock-deduction points — right-panel
     * labels change accordingly. Only the wording differs; behaviour is identical.
     */
    variant?: 'production' | 'consume';
}

const ProcessBuilder: React.FC<Props> = ({ options, selected, onChange, variant = 'production' }) => {
    const isConsume = variant === 'consume';
    const intl = useIntl();
    const [q, setQ] = useState('');
    const [dragId, setDragId] = useState<string | null>(null);
    const [overId, setOverId] = useState<string | null>(null);
    const [libDragOver, setLibDragOver] = useState(false);

    const selectedKeys = useMemo(() => new Set(selected.map((s) => String(s.value))), [selected]);

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        return options.filter((p) => !ql || (p.label || '').toLowerCase().includes(ql));
    }, [options, q]);

    const add = (item: ChipOption) => {
        if (!selectedKeys.has(String(item.value))) onChange([...selected, item]);
    };

    const remove = (id: string) => onChange(selected.filter((s) => String(s.value) !== id));

    const move = (idx: number, delta: -1 | 1) => {
        const target = idx + delta;
        if (target < 0 || target >= selected.length) return;
        const next = [...selected];
        const [item] = next.splice(idx, 1);
        next.splice(target, 0, item);
        onChange(next);
    };

    const reorder = (fromId: string, toId: string) => {
        if (fromId === toId) return;
        const fromIdx = selected.findIndex((s) => String(s.value) === fromId);
        const toIdx = selected.findIndex((s) => String(s.value) === toId);
        if (fromIdx < 0 || toIdx < 0) return;
        const next = [...selected];
        const [item] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, item);
        onChange(next);
    };

    const onLibDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const id = e.dataTransfer.getData('lib');
        if (id) {
            const item = options.find((o) => String(o.value) === id);
            if (item) add(item);
        }
        setLibDragOver(false);
    };

    const searchPlaceholder = intl.formatMessage({
        id: 'admin.articles.builder.searchPlaceholder',
        defaultMessage: 'Kërko procese...',
    });

    return (
        <div className="art-builder">
            {/* ── Library panel — flat list of available processes ──────────── */}
            <div className="art-builder__panel">
                <div className="art-builder__head">
                    <span className="art-builder__title">
                        <FormattedMessage id="admin.articles.builder.libraryTitle" defaultMessage="Proceset e disponueshme" />
                    </span>
                    <span className="art-builder__count">
                        <FormattedMessage
                            id="admin.articles.builder.processCount"
                            defaultMessage="{count, plural, one {# proces} other {# procese}}"
                            values={{ count: filtered.length }}
                        />
                    </span>
                </div>

                <div className="art-builder__search">
                    <svg className="art-builder__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.2-3.2" />
                    </svg>
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        aria-label={searchPlaceholder}
                    />
                </div>

                <div className="art-builder__lib">
                    {filtered.length === 0 ? (
                        <div className="art-builder__lib-empty">
                            <FormattedMessage id="admin.articles.builder.noMatch" defaultMessage="S'ka procese që përputhen" />
                        </div>
                    ) : (
                        filtered.map((p) => {
                            const added = selectedKeys.has(String(p.value));
                            return (
                                <div
                                    key={p.value}
                                    className={`art-builder__item ${added ? 'is-added' : ''}`}
                                    draggable={!added}
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('lib', String(p.value));
                                        e.dataTransfer.effectAllowed = 'copy';
                                    }}
                                    onClick={() => !added && add(p)}
                                    role="button"
                                    tabIndex={added ? -1 : 0}
                                    onKeyDown={(e) => {
                                        if (!added && (e.key === 'Enter' || e.key === ' ')) {
                                            e.preventDefault();
                                            add(p);
                                        }
                                    }}
                                >
                                    <span className="art-builder__item-name">{p.label}</span>
                                    <span className="art-builder__item-add" aria-hidden="true">
                                        {added ? (
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 12.5l4.5 4.5L19 7" />
                                            </svg>
                                        ) : (
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 5v14M5 12h14" />
                                            </svg>
                                        )}
                                    </span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ── Sequence panel — ordered production order (drop target) ───── */}
            <div className="art-builder__panel art-builder__panel--seq">
                <div className="art-builder__head">
                    <svg className="art-builder__head-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="6" cy="6" r="2.4" />
                        <circle cx="18" cy="18" r="2.4" />
                        <path d="M8.4 6H14a3 3 0 0 1 0 6h-4a3 3 0 0 0 0 6h5.6" />
                    </svg>
                    <span className="art-builder__title">
                        {isConsume
                            ? <FormattedMessage id="admin.articles.builder.consume.selectedTitle" defaultMessage="Proceset e zgjedhura" />
                            : <FormattedMessage id="admin.articles.builder.orderTitle" defaultMessage="Rendi i prodhimit" />}
                    </span>
                    <span className="art-builder__count art-builder__count--brand">
                        {isConsume
                            ? <FormattedMessage
                                id="admin.articles.builder.consume.count"
                                defaultMessage="{count, plural, one {# proces} other {# procese}}"
                                values={{ count: selected.length }}
                            />
                            : <FormattedMessage
                                id="admin.articles.builder.steps"
                                defaultMessage="{count, plural, one {# hap} other {# hapa}}"
                                values={{ count: selected.length }}
                            />}
                    </span>
                </div>

                <div
                    className={`art-builder__seq ${libDragOver ? 'is-drop-target' : ''}`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        if (e.dataTransfer.types.includes('lib')) setLibDragOver(true);
                    }}
                    onDragLeave={() => setLibDragOver(false)}
                    onDrop={onLibDrop}
                >
                    {selected.length === 0 ? (
                        <div className="art-builder__seq-empty">
                            <div className="art-builder__seq-empty-icon">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="6" cy="6" r="2.4" />
                                    <circle cx="18" cy="18" r="2.4" />
                                    <path d="M8.4 6H14a3 3 0 0 1 0 6h-4a3 3 0 0 0 0 6h5.6" />
                                </svg>
                            </div>
                            <div className="art-builder__seq-empty-title">
                                {isConsume
                                    ? <FormattedMessage id="admin.articles.builder.consume.emptyTitle" defaultMessage="Asnjë proces ende" />
                                    : <FormattedMessage id="admin.articles.builder.emptyTitle" defaultMessage="Asnjë hap ende" />}
                            </div>
                            <div className="art-builder__seq-empty-sub">
                                <FormattedMessage id="admin.articles.builder.emptySub" defaultMessage="Tërhiq ose kliko nga lista" />
                            </div>
                        </div>
                    ) : (
                        selected.map((item, idx) => {
                            const isDragging = dragId === String(item.value);
                            const isOver = overId === String(item.value) && dragId !== String(item.value);
                            return (
                                <div
                                    key={item.value}
                                    className={`art-builder__seq-row ${isDragging ? 'is-dragging' : ''} ${isOver ? 'is-over' : ''}`}
                                    draggable
                                    onDragStart={(e) => {
                                        setDragId(String(item.value));
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('seq', String(item.value));
                                    }}
                                    onDragEnd={() => { setDragId(null); setOverId(null); }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        if (dragId) setOverId(String(item.value));
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        if (dragId) reorder(dragId, String(item.value));
                                        setDragId(null);
                                        setOverId(null);
                                    }}
                                >
                                    <span className="art-builder__seq-handle" aria-hidden="true">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                            <path d="M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01" />
                                        </svg>
                                    </span>
                                    <span className="art-builder__seq-num">{idx + 1}</span>
                                    <div className="art-builder__seq-main">
                                        <div className="art-builder__seq-name">{item.label}</div>
                                    </div>

                                    {/* Touch reorder fallback — visible only on touch via media query */}
                                    <div className="art-builder__seq-reorder" onMouseDown={(e) => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            className="art-builder__seq-reorder-btn"
                                            aria-label="Move up"
                                            disabled={idx === 0}
                                            onClick={() => move(idx, -1)}
                                        >
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <polyline points="15 18 9 12 15 6" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            className="art-builder__seq-reorder-btn"
                                            aria-label="Move down"
                                            disabled={idx === selected.length - 1}
                                            onClick={() => move(idx, 1)}
                                        >
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <polyline points="9 6 15 12 9 18" />
                                            </svg>
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        className="art-builder__seq-remove"
                                        onClick={() => remove(String(item.value))}
                                        aria-label={intl.formatMessage({ id: 'admin.articles.builder.removeStep', defaultMessage: 'Hiq' })}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M6 6l12 12M18 6 6 18" />
                                        </svg>
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProcessBuilder;
